import { useMemo } from 'react';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters';
import {
	useViewUpdater,
	createViewUpdaterCallbacksWithCacheActions,
	useCacheActions,
	type UpdaterReturn,
} from '@atlassian/jira-business-list-handlers';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import type { DateOnly } from '@atlassian/jira-platform-utils-date-only';
import { useStartDateField } from '@atlassian/jira-router-resources-business-configuration';
import type {
	JWMCalendarItemDetails_jira_list_JiraWorkManagementViewItemConnection_edges_node as JWMCalendarItemDetailsNode,
	JWMCalendarItemDetails_jira_list_JiraWorkManagementViewItemConnection as JWMCalendarItemDetailsEdge,
	JWMCalendarItemDetails,
} from '../../services/calendar-view-items/__generated_apollo__/JWMCalendarItemDetails';
import { QUERY, CALENDAR_ITEM_DETAILS_QUERY } from '../../services/calendar-view-items/gql';
import { toQuery } from '../../services/calendar-view-items/utils';
import { CALENDAR_ITEM_FIELDS } from '../../services/constants';

export type CalendarViewUpdaterServiceProps = {
	request: {
		jqlContext: string;
		startDate: Date | DateOnly;
		endDate: Date | DateOnly;
	};
};

// Loads individual items, dealing with the cache updates
export const useCalendarViewUpdaterService = ({
	request,
}: CalendarViewUpdaterServiceProps): UpdaterReturn => {
	const { quickFiltersJql, isHidingDoneItems } = useFilteredViewContextInput({
		view: 'calendar',
	});

	const { addToCache, removeFromCache, updateItemsInCache } = useCacheActions();

	const { data: startDateField } = useStartDateField();
	let startDateFieldId = null;
	if (startDateField?.id) {
		[, startDateFieldId] = startDateField.id.split('_');
	}

	const jql = toQuery(request, quickFiltersJql, startDateFieldId);

	const variables = useMemo(
		() => ({
			fieldIds: CALENDAR_ITEM_FIELDS,
			jql,
		}),
		[jql],
	);

	const { addToCacheCallback, removeFromCacheCallback } =
		createViewUpdaterCallbacksWithCacheActions<JWMCalendarItemDetailsNode, JWMCalendarItemDetails>({
			query: QUERY,
			addToCache,
			updateItemsInCache,
			removeFromCache,
			// as calendar has no concept of hierarchy, hierarchyParent is always undefined
			// As such, these functions bellow should never be called
			isHierarchyEnabled: () => false,
			isInfiniteHierarchyEnabled: () => false,
			createVariablesForItemWithoutParent: () => variables,
			createVariablesForItemWithParent: () => variables,
			shouldChildItemBeAddedToOrRemovedFromCache: () => true,
			onUpdateParentWithNewChild: (issue) => issue,
		});

	return useViewUpdater<
		JWMCalendarItemDetailsNode,
		JWMCalendarItemDetailsEdge,
		JWMCalendarItemDetails
	>({
		input: {
			fieldIds: CALENDAR_ITEM_FIELDS,
			quickFiltersJql,
			isHidingDoneItems,
		},
		query: CALENDAR_ITEM_DETAILS_QUERY,
		actions: [
			{
				addToCache: (items) => addToCacheCallback(items),
				removeFromCache: (items) => removeFromCacheCallback(items),
			},
		],
		onError: (apolloError) => {
			fireErrorAnalytics({
				meta: {
					id: 'calendarViewUpdaterQuery',
					packageName: 'jiraBusinessCalendar',
					teamName: 'wanjel',
				},
				attributes: {
					message: 'Failed to fetch issues in calendar view updater',
				},
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		onAppendItemsError: (apolloError) => {
			fireErrorAnalytics({
				meta: {
					id: 'calendarViewAppendItemsQuery',
					packageName: 'jiraBusinessCalendar',
					teamName: 'wanjel',
				},
				attributes: {
					message: 'Failed to append issues by id in calendar view updater',
				},
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
	});
};
